import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Car,
  Truck,
  Caravan,
  Gem,
  Sparkles,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const vehicleTypes = [
  { id: "sedan", label: "Sedan / Hatch", Icon: Car },
  { id: "suv", label: "SUV", Icon: Car },
  { id: "ute", label: "Ute / 4WD", Icon: Truck },
  { id: "luxury", label: "Luxury / Performance", Icon: Gem },
  { id: "van", label: "Van / Commercial", Icon: Truck },
  { id: "caravan", label: "Caravan", Icon: Caravan },
];

const packages = [
  { id: "Basic Wash — $150", title: "Basic Wash", price: "$150", desc: "1–2 hrs · exterior refresh" },
  { id: "Interior + Exterior Detail — $350", title: "Interior + Exterior Detail", price: "$350", desc: "3–5 hrs · inside & out" },
  { id: "Buffing Package — $599", title: "Buffing Package", price: "$599", desc: "5–7 hrs · restore gloss" },
  { id: "Ceramic Coating + Buff + Clay Bar — $1250", title: "Ceramic + Buff + Clay Bar", price: "$1,250", desc: "7–10 hrs · ultimate protection" },
  { id: "9-Year Ceramic Coating — $2000", title: "9-Year Ceramic Coating", price: "$2,000", desc: "10–14 hrs · 9yr warranty" },
  { id: "Caravan Basic — from $350", title: "Caravan Basic", price: "from $350", desc: "pre/post-trip wash" },
  { id: "Caravan Wash & Polish — from $750", title: "Caravan Wash & Polish", price: "from $750", desc: "restore gloss · caravan" },
  { id: "Caravan Ultimate + Ceramic — from $1,500", title: "Caravan Ultimate", price: "from $1,500", desc: "ceramic protection · caravan" },
];

const addons = [
  "Engine Bay Detail (+$75)",
  "Pet Hair Removal (+$75)",
  "Fabric Seat Cleaning (+$85)",
  "Odour Elimination (+$75)",
  "Sand & Dry Grass Removal (+$75)",
  "Clay Bar Treatment",
  "Iron Decontamination",
  "Leather Conditioning",
  "Headlight Restoration",
  "Ceramic Spray Protection",
];

const initial = {
  name: "",
  phone: "",
  email: "",
  vehicle_type: "",
  vehicle_details: "",
  service: "",
  addons: [],
  preferred_date: "",
  preferred_time: "",
  location: "",
  notes: "",
};

export default function Booking({ promo = "", onSuccess }) {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleAddon = (name) =>
    setForm((f) => ({
      ...f,
      addons: f.addons.includes(name)
        ? f.addons.filter((a) => a !== name)
        : [...f.addons, name],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (
      !form.name ||
      !form.phone ||
      !form.email ||
      !form.vehicle_type ||
      !form.service ||
      !form.preferred_date ||
      !form.location
    ) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      // Compose a payload compatible with existing backend schema
      const vehicleLabel =
        vehicleTypes.find((v) => v.id === form.vehicle_type)?.label ||
        form.vehicle_type;
      const vehicle = form.vehicle_details
        ? `${vehicleLabel} — ${form.vehicle_details}`
        : vehicleLabel;
      const notesParts = [];
      if (promo) notesParts.push(`[VOUCHER: ${promo} — 10% OFF FIRST DETAIL]`);
      if (form.addons.length)
        notesParts.push(`Add-ons: ${form.addons.join(", ")}`);
      if (form.notes) notesParts.push(form.notes);
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email,
        vehicle,
        service: form.service,
        preferred_date: form.preferred_date,
        preferred_time: form.preferred_time,
        location: form.location,
        notes: notesParts.join("\n"),
      };

      const dbSave = axios
        .post(`${API}/bookings`, payload)
        .catch((e) => console.warn("DB save failed:", e?.message || e));

      // Web3Forms fire-and-forget
      const fd = new FormData();
      fd.append("access_key", "eeceb683-230f-4289-8e1e-39c6d94acadd");
      fd.append("subject", `New Booking — ${payload.name} (${payload.service})`);
      fd.append("from_name", "Deluxe Diamond Detailing Website");
      fd.append("replyto", payload.email);
      fd.append("name", payload.name);
      fd.append("phone", payload.phone);
      fd.append("email", payload.email);
      fd.append("vehicle", payload.vehicle);
      fd.append("service", payload.service);
      fd.append("preferred_date", payload.preferred_date);
      fd.append("preferred_time", payload.preferred_time || "—");
      fd.append("location", payload.location);
      fd.append("add_ons", form.addons.join(", ") || "—");
      fd.append("notes", payload.notes || "—");
      fd.append("voucher", promo || "—");
      const web3 = fetch("https://api.web3forms.com/submit", {
        method: "POST",
        mode: "no-cors",
        body: fd,
      }).catch(() => null);

      await Promise.all([dbSave, web3]);

      setForm(initial);
      if (onSuccess) {
        onSuccess({
          name: payload.name,
          service: payload.service,
          preferred_date: payload.preferred_date,
          preferred_time: payload.preferred_time,
        });
      } else {
        toast.success("Booking sent. We'll be in touch shortly.");
      }
    } catch (err) {
      toast.error("Could not submit. Please call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="booking-section"
      className="section bg-[color:var(--surface-2)] relative"
    >
      <div className="max-w-[1400px] mx-auto flex flex-col gap-10 lg:grid lg:grid-cols-12">
        <div className="col-span-12 lg:col-span-4">
          <div className="flex items-center gap-3 mb-6 reveal">
            <span className="diamond-divider" />
            <span className="overline">Book A Detail</span>
          </div>
          <h2 className="reveal font-display tracking-tight text-neutral-900 text-4xl md:text-5xl lg:text-6xl leading-[1.05]">
            Your vehicle
            <br />
            deserves
            <br />
            <span className="text-neutral-500 font-light italic">premium care.</span>
          </h2>
          <p className="mt-6 text-neutral-600 leading-relaxed max-w-md reveal">
            Tap your vehicle type, choose a package and any add-ons — we handle
            the rest. Takes under a minute.
          </p>

          {promo && (
            <div
              data-testid="booking-promo-badge"
              className="mt-6 flex items-center gap-3 p-4 border border-[color:var(--accent)]/60 bg-[color:var(--accent)]/5"
            >
              <div className="font-mono uppercase text-[color:var(--accent)] text-xs tracking-[0.25em]">
                {promo}
              </div>
              <div className="text-neutral-800 text-sm">
                10% off your first detail — applied.
              </div>
            </div>
          )}

          <div className="mt-10 space-y-3">
            <a
              href="tel:0450539879"
              data-testid="contact-phone-1"
              className="flex items-center gap-4 group p-4 border border-neutral-200 bg-white hover:border-[color:var(--accent)] transition-colors"
            >
              <Phone size={20} className="text-[color:var(--accent)]" />
              <div>
                <div className="overline">Call us</div>
                <div className="font-display text-neutral-900 text-lg">0450 539 879</div>
              </div>
              <ArrowRight size={16} className="ml-auto text-neutral-300 group-hover:text-[color:var(--accent)] transition-colors" />
            </a>
            <a
              href="tel:0478955883"
              data-testid="contact-phone-2"
              className="flex items-center gap-4 group p-4 border border-neutral-200 bg-white hover:border-[color:var(--accent)] transition-colors"
            >
              <Phone size={20} className="text-[color:var(--accent)]" />
              <div>
                <div className="overline">Alt line</div>
                <div className="font-display text-neutral-900 text-lg">0478 955 883</div>
              </div>
            </a>
            <a
              href="mailto:deluxe.diamond.detailing0@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-4 group p-4 border border-neutral-200 bg-white hover:border-[color:var(--accent)] transition-colors"
            >
              <Mail size={20} className="text-[color:var(--accent)]" />
              <div>
                <div className="overline">Email</div>
                <div className="font-display text-neutral-900 text-sm md:text-base break-all">
                  deluxe.diamond.detailing0@gmail.com
                </div>
              </div>
            </a>
            <div className="flex items-center gap-4 p-4 border border-neutral-200 bg-white">
              <MapPin size={20} className="text-[color:var(--accent)]" />
              <div>
                <div className="overline">Service Area</div>
                <div className="font-display text-neutral-900 text-sm md:text-base">
                  Fully mobile · We come to you
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-8 reveal">
          <form
            data-testid="booking-form"
            onSubmit={submit}
            className="card p-6 md:p-10 space-y-8 rounded-sm"
          >
            {/* Step 1: Vehicle Type */}
            <div>
              <StepTitle n="01" title="What are we detailing?" />
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
                {vehicleTypes.map((v) => {
                  const active = form.vehicle_type === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      data-testid={`vehicle-type-${v.id}`}
                      onClick={() => update("vehicle_type", v.id)}
                      className={`relative flex flex-col items-center justify-center gap-2.5 p-5 border-2 transition-all duration-200 ${
                        active
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 shadow-md"
                          : "border-neutral-200 bg-white hover:border-[color:var(--accent)]/60 hover:bg-[color:var(--surface-2)]"
                      }`}
                    >
                      <v.Icon
                        size={28}
                        strokeWidth={1.5}
                        className={
                          active
                            ? "text-[color:var(--accent)]"
                            : "text-neutral-500"
                        }
                      />
                      <span
                        className={`text-sm font-medium ${
                          active
                            ? "text-[color:var(--accent)]"
                            : "text-neutral-800"
                        }`}
                      >
                        {v.label}
                      </span>
                      {active && (
                        <Check
                          size={16}
                          className="absolute top-2 right-2 text-[color:var(--accent)]"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              <input
                data-testid="vehicle-details"
                className="input w-full px-4 py-3 mt-4"
                value={form.vehicle_details}
                onChange={(e) => update("vehicle_details", e.target.value)}
                placeholder="Optional: year / make / model (e.g. 2023 BMW X5)"
              />
            </div>

            <Divider />

            {/* Step 2: Package */}
            <div>
              <StepTitle n="02" title="Choose your package" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">
                {packages.map((p) => {
                  const active = form.service === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`pkg-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => update("service", p.id)}
                      className={`relative text-left p-5 border-2 transition-all duration-200 ${
                        active
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 shadow-md"
                          : "border-neutral-200 bg-white hover:border-[color:var(--accent)]/60 hover:bg-[color:var(--surface-2)]"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-display text-neutral-900 font-semibold text-base leading-tight">
                          {p.title}
                        </div>
                        <div className="text-[color:var(--accent)] font-mono text-sm font-bold whitespace-nowrap">
                          {p.price}
                        </div>
                      </div>
                      <div className="text-xs text-neutral-500 mt-2">
                        {p.desc}
                      </div>
                      {active && (
                        <Check
                          size={16}
                          className="absolute top-2 right-2 text-[color:var(--accent)]"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <Divider />

            {/* Step 3: Add-ons */}
            <div>
              <StepTitle n="03" title="Add-ons (optional)" />
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mt-5">
                {addons.map((a) => {
                  const active = form.addons.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      data-testid={`addon-chip-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => toggleAddon(a)}
                      className={`relative p-3.5 text-center text-sm border-2 transition-all duration-200 ${
                        active
                          ? "border-[color:var(--accent)] bg-[color:var(--accent)]/10 text-[color:var(--accent)] font-semibold shadow-md"
                          : "border-neutral-200 bg-white text-neutral-700 hover:border-[color:var(--accent)]/60 hover:bg-[color:var(--surface-2)]"
                      }`}
                    >
                      {active && (
                        <Check
                          size={14}
                          className="absolute top-1.5 right-1.5 text-[color:var(--accent)]"
                        />
                      )}
                      {a}
                    </button>
                  );
                })}
              </div>
            </div>

            <Divider />

            {/* Step 4: Date / Time / Location */}
            <div>
              <StepTitle n="04" title="When & where" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                <Field label="Date *">
                  <input
                    data-testid="booking-date"
                    type="date"
                    className="input w-full px-4 py-3"
                    value={form.preferred_date}
                    onChange={(e) => update("preferred_date", e.target.value)}
                  />
                </Field>
                <Field label="Time">
                  <input
                    data-testid="booking-time"
                    type="time"
                    className="input w-full px-4 py-3"
                    value={form.preferred_time}
                    onChange={(e) => update("preferred_time", e.target.value)}
                  />
                </Field>
                <Field label="Location *">
                  <input
                    data-testid="booking-location"
                    className="input w-full px-4 py-3"
                    value={form.location}
                    onChange={(e) => update("location", e.target.value)}
                    placeholder="Suburb / address"
                  />
                </Field>
              </div>
            </div>

            <Divider />

            {/* Step 5: Contact */}
            <div>
              <StepTitle n="05" title="Your details" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                <Field label="Full name *">
                  <input
                    data-testid="booking-name"
                    className="input w-full px-4 py-3"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="John Smith"
                  />
                </Field>
                <Field label="Phone *">
                  <input
                    data-testid="booking-phone"
                    className="input w-full px-4 py-3"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="0400 000 000"
                  />
                </Field>
                <Field label="Email *" className="md:col-span-2">
                  <input
                    data-testid="booking-email"
                    type="email"
                    className="input w-full px-4 py-3"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    placeholder="you@email.com"
                  />
                </Field>
                <Field label="Notes" className="md:col-span-2">
                  <textarea
                    data-testid="booking-notes"
                    rows={3}
                    className="textarea w-full px-4 py-3 resize-none"
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    placeholder="Anything we should know? Access info, condition, etc."
                  />
                </Field>
              </div>
            </div>

            <div className="pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-neutral-500 max-w-sm">
                By submitting, you agree to be contacted by Deluxe Diamond
                Detailing about your booking.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="booking-submit"
                className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending…" : "Send Booking Request"}
                {!submitting && <ArrowRight size={16} />}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function StepTitle({ n, title }) {
  return (
    <div className="flex items-center gap-3">
      <span className="font-mono text-[color:var(--accent)] text-xs tracking-[0.3em]">
        {n}
      </span>
      <span className="font-display text-neutral-900 text-lg md:text-xl font-semibold">
        {title}
      </span>
    </div>
  );
}

function Divider() {
  return <div className="h-px bg-neutral-200" />;
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="overline mb-2 block">{label}</span>
      {children}
    </label>
  );
}
